// ─── ID Generation ─────────────────────────────────────────────────────────
export const generateId = () => Math.random().toString(36).substr(2, 9) + Date.now().toString(36);

// ─── Currency ──────────────────────────────────────────────────────────────
export const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return 'Rp 0';
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(amount);
};

export const parseCurrency = (str) => parseInt(str.replace(/\D/g, ''), 10) || 0;

// ─── Date & Time ───────────────────────────────────────────────────────────
export const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' });
};

export const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export const formatDateShort = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const isToday = (dateStr) => {
  const today = new Date();
  const d = new Date(dateStr);
  return d.toDateString() === today.toDateString();
};

export const daysBetweenDates = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return Math.abs(Math.floor((d2 - d1) / (1000 * 60 * 60 * 24)));
};

export const getMonthName = (monthIndex) => {
  const months = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
  return months[monthIndex];
};

// ─── Phone ─────────────────────────────────────────────────────────────────
export const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.startsWith('08')) return cleaned.replace(/(\d{4})(\d{4})(\d{4})/, '$1-$2-$3');
  return phone;
};

// ─── Validation ────────────────────────────────────────────────────────────
export const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
export const validatePhoneNumber = (phone) => /^(\+62|62|0)[8][1-9][0-9]{7,11}$/.test(phone.replace(/\s|-/g, ''));

// ─── Sorting & Filtering ───────────────────────────────────────────────────
export const sortByField = (arr, field, direction = 'asc') => {
  return [...arr].sort((a, b) => {
    const va = a[field]; const vb = b[field];
    if (direction === 'asc') return va < vb ? -1 : va > vb ? 1 : 0;
    return va > vb ? -1 : va < vb ? 1 : 0;
  });
};

export const filterByMultipleFields = (arr, query, fields) => {
  if (!query) return arr;
  const q = query.toLowerCase();
  return arr.filter(item => fields.some(f => String(item[f] || '').toLowerCase().includes(q)));
};

export const groupByField = (arr, field) => {
  return arr.reduce((acc, item) => {
    const key = item[field];
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
};

// ─── Math ──────────────────────────────────────────────────────────────────
export const calculatePercentage = (value, total) => {
  if (!total) return 0;
  return Math.round((value / total) * 100);
};

export const calculateAverage = (arr, field) => {
  if (!arr.length) return 0;
  return arr.reduce((sum, item) => sum + (item[field] || 0), 0) / arr.length;
};

// ─── Export ────────────────────────────────────────────────────────────────
export const exportToCSV = (data, filename = 'export') => {
  if (!data.length) return;
  const headers = Object.keys(data[0]);
  const rows = data.map(row => headers.map(h => `"${String(row[h] || '').replace(/"/g, '""')}"`).join(','));
  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = `${filename}.csv`; a.click();
  URL.revokeObjectURL(url);
};

export const exportToJSON = (data, filename = 'export') => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = `${filename}.json`; a.click();
  URL.revokeObjectURL(url);
};

// ─── Clipboard ─────────────────────────────────────────────────────────────
export const copyToClipboard = async (text) => {
  try { await navigator.clipboard.writeText(text); return true; }
  catch { return false; }
};

// ─── Debounce / Throttle ───────────────────────────────────────────────────
export const debounce = (fn, delay) => {
  let timer;
  return (...args) => { clearTimeout(timer); timer = setTimeout(() => fn(...args), delay); };
};

export const throttle = (fn, limit) => {
  let lastCall = 0;
  return (...args) => { const now = Date.now(); if (now - lastCall >= limit) { lastCall = now; fn(...args); } };
};

// ─── SPK Number Generator ──────────────────────────────────────────────────
export const generateSPKNumber = (existingCount) => {
  const year = new Date().getFullYear();
  const num = String(existingCount + 1).padStart(3, '0');
  return `SPK-${year}-${num}`;
};

// ─── Lead Score ────────────────────────────────────────────────────────────
export const getLeadScore = (prospect) => {
  let score = 0;
  if (prospect.status === 'Hot') score += 50;
  else if (prospect.status === 'Warm') score += 30;
  else score += 10;
  if (prospect.sumberLead === 'Referral') score += 30;
  else if (prospect.sumberLead === 'Walk-in') score += 20;
  else score += 10;
  if (prospect.noHP) score += 20;
  return Math.min(score, 100);
};

// ─── Honda Car Prices ──────────────────────────────────────────────────────
export const hondaPrices = {
  'Honda Brio S MT': 165000000,
  'Honda Brio S CVT': 180000000,
  'Honda Brio RS MT': 195000000,
  'Honda Brio RS CVT': 210000000,
  'Honda WR-V S MT': 265000000,
  'Honda WR-V E CVT': 285000000,
  'Honda WR-V RS CVT': 310000000,
  'Honda HR-V 1.5 S CVT': 355000000,
  'Honda HR-V 1.5 E CVT': 380000000,
  'Honda HR-V 1.5 SE CVT': 420000000,
  'Honda HR-V RS CVT': 450000000,
  'Honda CR-V 1.5T CVT': 520000000,
  'Honda CR-V 1.5T Prestige': 575000000,
  'Honda Jazz RS CVT': 285000000,
  'Honda Mobilio S MT': 210000000,
  'Honda Mobilio E MT': 225000000,
  'Honda BR-V E M/T': 265000000,
  'Honda Odyssey 2.4': 750000000,
  'Honda Accord 1.5T': 800000000,
  'Honda City Hatchback RS': 335000000,
};
